import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import Button from "../../../components/common/Button";
import VideoPlayer from "../../../video/VideoPlayer";
import useVideoPicker from "../../../video/useVideoPicker";
import { useCreateFlow } from "../../../flow/CreateFlowContext";
import delet from "../../../assets/tips/delete.svg";
import eye from "../../../assets/posts/eye.svg";
import uploadIcon from "../../../assets/posts/upload.svg";

/**
 * Video media editor.
 *   onRemoved()      — a NEW video was deleted; the parent can go back to the upload step
 *   onPreviewClick() — LinkedIn-style preview
 *   onPick(info)     — a replacement video was chosen ({ file, duration, poster }); starts the upload
 *   isEditMode       — editing a saved post: shows its current video, with Replace / Remove
 */
export default function VideoSection({ onRemoved, onPreviewClick, onPick, isEditMode = false }) {
  const { t } = useTranslation();
  const { videoUpload } = useCreateFlow();
  const { status, progress, error, previewUrl, posterPreviewUrl, fileName, saved, savedRemoved } = videoUpload;
  const picker = useVideoPicker({ onConfirm: onPick });
  const [notice, setNotice] = useState(null);
  const [removing, setRemoving] = useState(false);

  const percent = Math.round(progress * 100);
  const toolbarClass =
    "flex flex-wrap items-center justify-start sm:justify-end gap-x-2 gap-y-0 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full border border-b-0 border-gray-200";
  const errorBox = "mt-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3";

  // ===== Editing a post: no upload in progress =====
  if (status === "idle") {
    if (!isEditMode) return null;
    return (
      <div id="section-video" className="mb-4 relative">
        <input
          type="file"
          ref={picker.inputRef}
          accept="video/mp4,.mp4"
          onChange={picker.handleFile}
          className="hidden"
        />
        <p className="text-lg sm:text-xl font-bold text-black-800 mb-3">
          {t("video.editor.title")}
        </p>

        {saved ? (
          <>
            <div className={toolbarClass}>
              <ToolbarTooltipButton label={t("video.editor.replace")} showLabel onClick={picker.openPicker}>
                <img src={uploadIcon} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton label={t("video.editor.remove")} showLabel onClick={videoUpload.removeSaved}>
                <img src={delet} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
            </div>
            <div className="rounded-bl-lg rounded-br-lg overflow-hidden">
              <VideoPlayer key={saved.video_url} src={saved.video_url} posterUrl={saved.video_poster_url} />
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-black-40 bg-black-20 p-4">
            <p className="text-black-800 mb-3">{t("video.editor.removedNote")}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="dark" onClick={picker.openPicker} className="w-full sm:w-auto">
                {t("video.upload")}
              </Button>
              {savedRemoved && (
                <Button variant="ghost" onClick={videoUpload.restoreSaved} className="w-full sm:w-auto">
                  {t("video.editor.keepOriginal")}
                </Button>
              )}
            </div>
          </div>
        )}

        {picker.error && <div className={errorBox}>{picker.error}</div>}
        {picker.modal}
      </div>
    );
  }

  // ===== A new video: uploading, done or failed =====
  const errorMessage = () => {
    if (!error) return "";
    if (error.code === "QUOTA_EXCEEDED") {
      return t("video.errors.quota", {
        used: error.quota?.used ?? "",
        limit: error.quota?.limit ?? "",
      });
    }
    if (error.code === "NETWORK" || error.code === "UPLOAD_FAILED") {
      return t("video.errors.network");
    }
    return error.message || t("video.errors.generic");
  };

  // Cancel during the upload, or remove after it: both delete the NEW video on the server.
  // When editing, the post's original video comes back afterwards.
  const remove = async () => {
    setRemoving(true);
    setNotice(null);
    const result = await videoUpload.discardVideo();
    setRemoving(false);
    if (result.code === "VIDEO_BUSY") {
      setNotice(t("video.errors.busy"));
      return;
    }
    onRemoved?.();
  };

  return (
    <div id="section-video" className="mb-4 relative">
      <p className="text-lg sm:text-xl font-bold text-black-800 mb-3">
        {t("video.editor.title")}
      </p>

      <div className={toolbarClass}>
        <ToolbarTooltipButton label={t("insights.preview")} onClick={onPreviewClick}>
          <img src={eye} alt="" className="w-4 h-4" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={isEditMode && saved ? t("video.editor.useOriginal") : t("video.editor.remove")}
          showLabel
          onClick={remove}
          disabled={removing}
        >
          <img src={delet} alt="" className="w-4 h-4" />
        </ToolbarTooltipButton>
      </div>

      <div className="w-full bg-black-900 rounded-bl-lg rounded-br-lg overflow-hidden">
        <video
          key={previewUrl}
          src={previewUrl}
          poster={posterPreviewUrl || undefined}
          controls
          playsInline
          preload="metadata"
          className="w-full max-h-[480px] block mx-auto"
        />
      </div>
      <p className="text-sm text-black-400 mt-2 truncate">{fileName}</p>

      {status === "uploading" && (
        <div className="mt-3">
          <p className="text-sm text-black-600 mb-1">
            {progress >= 1
              ? t("video.editor.finishing")
              : t("video.editor.uploading", { percent })}
          </p>
          <div className="h-2 rounded-full bg-black-40 overflow-hidden">
            <div
              className="h-full bg-primary-400 transition-all duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          <Button
            variant="ghost"
            onClick={remove}
            disabled={removing}
            className="w-auto u-btn-sm mt-3"
          >
            {t("video.editor.cancel")}
          </Button>
        </div>
      )}

      {status === "done" && (
        <p className="mt-3 text-black-800 font-bold">{t("video.editor.done")}</p>
      )}

      {status === "error" && <div className={errorBox}>{errorMessage()}</div>}
      {notice && <div className={errorBox}>{notice}</div>}
    </div>
  );
}